import {Stack, Table, Text, Title} from '@mantine/core'

import type {AssignedUnassignedData, EngagementLevel} from 'src/api/client/models/analytics'
import type {ProspectClickFilters} from 'src/components/analytics/use-prospect-navigation'
import {SectionCard} from 'src/components/section-card'
import {formatNumber} from 'src/lib/format'

const ENGAGEMENT_LEVELS: readonly EngagementLevel[] = [
    'Very Engaged',
    'Engaged',
    'Somewhat Engaged',
    'Minimally Engaged',
    'Disengaged',
]

type Props = {
    data: AssignedUnassignedData
    onProspectFilter?: (filters: ProspectClickFilters) => void
}

function MatrixTable({
    rows,
    label,
    onProspectFilter,
    titleMt = 'md',
}: {
    rows: AssignedUnassignedData['assigned']
    label: string
    onProspectFilter?: (filters: ProspectClickFilters) => void
    titleMt?: 'md' | 0
}) {
    return (
        <>
            <Title order={5} mt={titleMt}>
                {label}
            </Title>
            <Table style={{minWidth: 1000}} striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th style={{width: 200}}>Capacity Bucket</Table.Th>
                        {ENGAGEMENT_LEVELS.map((eng) => (
                            <Table.Th key={eng} style={{width: 120, textAlign: 'right'}}>
                                {eng}
                            </Table.Th>
                        ))}
                        <Table.Th style={{width: 90, textAlign: 'right'}}>Total</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.map((row) => (
                        <Table.Tr key={row.capacityBucket}>
                            <Table.Td
                                onClick={
                                    onProspectFilter
                                        ? () =>
                                              onProspectFilter({
                                                  capacityBucket: row.capacityBucket,
                                              })
                                        : undefined
                                }
                                style={onProspectFilter ? {cursor: 'pointer'} : undefined}
                            >
                                {row.capacityBucket}
                            </Table.Td>
                            {ENGAGEMENT_LEVELS.map((eng) => (
                                <Table.Td
                                    key={eng}
                                    onClick={
                                        onProspectFilter
                                            ? () =>
                                                  onProspectFilter({
                                                      capacityBucket: row.capacityBucket,
                                                      engagementLevel: eng,
                                                  })
                                            : undefined
                                    }
                                    style={
                                        onProspectFilter
                                            ? {textAlign: 'right', cursor: 'pointer'}
                                            : {textAlign: 'right'}
                                    }
                                >
                                    {formatNumber(row[eng] || 0)}
                                </Table.Td>
                            ))}
                            <Table.Td
                                onClick={
                                    onProspectFilter
                                        ? () =>
                                              onProspectFilter({
                                                  capacityBucket: row.capacityBucket,
                                              })
                                        : undefined
                                }
                                style={
                                    onProspectFilter
                                        ? {textAlign: 'right', cursor: 'pointer'}
                                        : {textAlign: 'right'}
                                }
                            >
                                <strong>{formatNumber(row.total)}</strong>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
                <Table.Tfoot>
                    <Table.Tr>
                        <Table.Td>
                            <strong>Total</strong>
                        </Table.Td>
                        {ENGAGEMENT_LEVELS.map((eng) => (
                            <Table.Td key={eng} style={{textAlign: 'right'}}>
                                <strong>
                                    {formatNumber(rows.reduce((s, r) => s + (r[eng] || 0), 0))}
                                </strong>
                            </Table.Td>
                        ))}
                        <Table.Td style={{textAlign: 'right'}}>
                            <strong>{formatNumber(rows.reduce((s, r) => s + r.total, 0))}</strong>
                        </Table.Td>
                    </Table.Tr>
                </Table.Tfoot>
            </Table>
        </>
    )
}

export function AssignedUnassignedExportContent({data}: {data: AssignedUnassignedData}) {
    return (
        <Stack gap="md">
            <Text size="sm" c="dimmed">
                {formatNumber(data.totalAssigned)} assigned · {formatNumber(data.totalUnassigned)}{' '}
                unassigned
            </Text>
            <MatrixTable rows={data.assigned} label="Assigned Prospects" titleMt={0} />
            <MatrixTable rows={data.unassigned} label="Unassigned Prospects" />
        </Stack>
    )
}

export default function AssignedUnassigned({data, onProspectFilter}: Props) {
    const sectionTitle = `Assigned vs Unassigned (${formatNumber(data.totalAssigned)} assigned, ${formatNumber(data.totalUnassigned)} unassigned)`
    return (
        <SectionCard title={sectionTitle}>
            <MatrixTable
                rows={data.assigned}
                label="Assigned Prospects"
                onProspectFilter={onProspectFilter}
            />
            <MatrixTable
                rows={data.unassigned}
                label="Unassigned Prospects"
                onProspectFilter={onProspectFilter}
            />
        </SectionCard>
    )
}
